import { ReactNode, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { Adg4Button } from "@/components/adg4/button/src";
import { ProductKey } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { wacToken } from "@/libraries/design/foundations";

import { ExistingUser } from "../existing-user";

import { ReturnToExistingProduct } from "./components";

export const WeekOneUser = () => {
    const createNewWorkspace = useMemo(() => {
        if (typeof window === "undefined") return false;
        const queryParams = new URLSearchParams(window.location.search);
        return queryParams.get("createNew") === "true";
    }, []);

    const [showSiteNaming, setShowSiteNaming] = useState(createNewWorkspace);
    const { product } = useUserContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const clickReturnToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "contactExpandGoToReturnToProductScreen",
            attributes: {
                productName: product?.name
            }
        }).fire();
    };

    const goToExistingWorkspaceMessageValues = {
        link: (str: ReactNode) => link(str, clickReturnToExistingWorkspace)
    };

    const goToExistingWorkspaceMessage =
        product?.key === ProductKey.CONFLUENCE
            ? intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedGoToExistingWorkspace",
                      defaultMessage:
                          "or <link>visit an existing workspace</link>"
                  },
                  goToExistingWorkspaceMessageValues
              )
            : intl.formatMessage(
                  {
                      id: "bxpSignup.companyExpand.cobrandedReturnToJoinSites",
                      defaultMessage: "or <link>join an existing site</link>"
                  },
                  goToExistingWorkspaceMessageValues
              );

    return showSiteNaming ? (
        <div css={containerCss}>
            <ExistingUser />
            <div css={goToExistingWorkspaceMessageCss}>
                {goToExistingWorkspaceMessage}
            </div>
        </div>
    ) : (
        <ReturnToExistingProduct
            productKeyForExpand={product?.key || ProductKey.JIRA_SOFTWARE}
            exitView={() => {
                setShowSiteNaming(true);
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "contactExpandGoToSiteNamingScreen",
                    attributes: {
                        productName: product?.name
                    }
                }).fire();
            }}
        />
    );
};

const containerCss = css({
    alignContent: "space-between",
    display: "grid",
    flexGrow: 1
});

const goToExistingWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#1558BC !important"
    }
});

const goToExistingWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});

const link = (str: ReactNode, clickReturnToExistingWorkspace: () => void) => (
    <Adg4Button
        css={goToExistingWorkspaceLinkCss}
        onClick={clickReturnToExistingWorkspace}
    >
        {str}
    </Adg4Button>
);
